import React, { useState, useRef, useEffect } from 'react';
import { apiCall } from '@/api/apiCall';
import { toast } from 'sonner';
import { useApiQueue } from '@/hooks/useApiQueue';
import CarPark from '@/assets/img/TaifContract-carPark.png';
import OnStreet from '@/assets/img/TaifContract-onStreet.png';
import Enforcement from '@/assets/img/TaifContract-Enforcement.png';
import { AssetsPanel } from '../../components/shared/AssetsPanel';
import LOGO from '@/assets/img/Makkah_1.png';
import LoadingComponent from '@/components/common/LoadingComponent';
import { API_CALL_TIME } from '@/constants/constant';

// ═══════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════
const groupsToFlat = (groups = []) => {
  const result = [];
  groups.forEach(g => {
    result.push({ name: g.group_name || '', isCategory: true, subCount: '', totalCount: String(g.group_total_qty ?? '-') });
    (g.items || []).forEach(item => {
      result.push({ name: item.item_name || '', isCategory: false, subCount: String(item.qty ?? '-'), totalCount: '' });
    });
  });
  return result;
};

const flatToGroups = (flat) => {
  const groups = [];
  let cur = null;
  flat.forEach(a => {
    if (a.isCategory) {
      if (cur) groups.push(cur);
      cur = { group_name: a.name, group_total_qty: a.totalCount || '', items: [] };
    } else if (cur) {
      cur.items.push({ item_name: a.name, qty: a.subCount || '' });
    }
  });
  if (cur) groups.push(cur);
  return groups;
};

// ═══════════════════════════════════════════════════════════════════════
// ASSET GROUPS HELPER
// ═══════════════════════════════════════════════════════════════════════
function makeAssetGroups(o = {}) {
  return [
    { group_name: 'Ticket Dispenser', group_total_qty: o.ticketDispenser ?? '-', items: [{ item_name: 'DIP G100', qty: '-' }, { item_name: 'G100 64 Bit', qty: o.g10064 ?? '-' }, { item_name: 'G100 32 Bit', qty: '-' }] },
    { group_name: 'Ticket Verifier', group_total_qty: o.ticketVerifier ?? '-', items: [{ item_name: 'EV DIP G100', qty: '-' }, { item_name: 'EV G100 64 Bit', qty: o.evG10064 ?? '-' }, { item_name: 'EV G100 32 Bit', qty: '-' }] },
    { group_name: "APM's", group_total_qty: o.apms ?? '-', items: [{ item_name: 'APM ETS-70', qty: '-' }, { item_name: 'APM ETS-100', qty: o.apmEts100 ?? '-' }] },
    { group_name: 'PIN Pad', group_total_qty: o.pinPad ?? '-', items: [{ item_name: 'IUP 250', qty: o.iup250 ?? '-' }] },
    { group_name: 'Card Reader', group_total_qty: o.cardReader ?? '-', items: [{ item_name: 'IUR250', qty: o.iur250 ?? '-' }] },
    { group_name: 'B-Contactless', group_total_qty: o.bContactless ?? '-', items: [{ item_name: 'IUC 150', qty: o.iuc150 ?? '-' }] },
    { group_name: 'RFID Reader', group_total_qty: o.rfidReader ?? '-', items: [] },
    { group_name: 'IP Barrier', group_total_qty: o.ipBarrier ?? '-', items: [] },
    { group_name: 'ANPR CAMERA', group_total_qty: o.anprCamera ?? '-', items: [{ item_name: 'ARH', qty: o.arh ?? '-' }, { item_name: 'Luminera', qty: o.luminera ?? '-' }] },
    { group_name: 'Sliding Gate', group_total_qty: o.slidingGate ?? '-', items: [{ item_name: 'RIGHT', qty: o.right ?? '-' }, { item_name: 'LEFT', qty: o.left ?? '-' }] },
    { group_name: "PND's", group_total_qty: o.pnds ?? '-', items: [{ item_name: 'TPAL- BNA Large', qty: o.tpalBnaLarge ?? '-' }, { item_name: 'T-PAL TX', qty: o.tpalTx ?? '-' }, { item_name: 'T-PAL V2', qty: o.tpalV2 ?? '-' }] },
    { group_name: 'Pound Cahier Device', group_total_qty: o.poundCahier ?? '-', items: [{ item_name: 'CN-50', qty: '-' }] },
    { group_name: 'Driver Device', group_total_qty: '-', items: [{ item_name: 'CN-51', qty: '-' }] },
    { group_name: 'Enf Device', group_total_qty: o.enfDevice ?? '-', items: [{ item_name: 'Oukitel WP 2', qty: o.oukitelWp2 ?? '-' }, { item_name: 'Doogee V10', qty: o.doogeeV10 ?? '-' }] },
    { group_name: 'Validation', group_total_qty: o.validation ?? '-', items: [{ item_name: 'Lenova Tablet', qty: '-' }] },
    { group_name: 'Printer', group_total_qty: o.printer ?? '-', items: [{ item_name: 'PR-3', qty: o.pr3 ?? '-' }, { item_name: 'SEWOO', qty: o.sewoo ?? '-' }, { item_name: 'BIXOLON', qty: o.bixolon ?? '-' }] },
    { group_name: 'Tow Truck', group_total_qty: o.towTruck ?? '-', items: [] },
    { group_name: 'Clamp Vehicle', group_total_qty: o.clampVehicle ?? '-', items: [] },
    { group_name: 'Support Vehicle', group_total_qty: o.supportVehicle ?? '-', items: [] },
  ];
}

// ═══════════════════════════════════════════════════════════════════════
// CONFIG
// ═══════════════════════════════════════════════════════════════════════
const MAKKAH_PROFILE_CONFIG = {
  makkah_on_street_profile: {
    profileKey: 'makkah_on_street_profile',
    violationKey: 'makkah_on_street_violations',
    assetsKey: 'makkah_on_street_assets',
    tarifKey: 'makkah_on_street_tariff',
    pageType: 'on_street',
    profileFallback: {
      table_key: 'makkah_on_street_profile',
      title: 'Makkah On Street - Profile',
      data: {
        total_bays: { label: 'Total On-Street Parking Bays', value: '7,123' },
        contract_term: { value: '25 years' },
        contract_period: { start_date: '02.11.2014', end_date: '02.02.2039' },
        contract_note: { value: '(Rent payment started 19.01.2016- 15 months grace period)' },
        operational_hours: { value: 'zonewise as per below table' },
        parking_tariff: { value: 'as per below table' },
        ramadan_hours: { value: '24 hours' },
      },
    },
    violationFallback: {
      table_key: 'makkah_on_street_violations',
      title: 'Makkah On Street Violation Summary (Excluding Vat)',
      data: {
        rows: [
          { city_code: 'MAK', violation_code: '1', violation_name: 'THE USE OF PARKING SPACE WITHOUT PAYING THE PARKING FEE', ticket_charges: '200', tow_charges: '250', clamp_charges: '0' },
          { city_code: 'MAK', violation_code: '2', violation_name: 'THE EXPIRY OF THE PAID PARKING PERIOD WITHOUT VACATING THE PARKING SPACE', ticket_charges: '100', tow_charges: '-', clamp_charges: '0' },
          { city_code: 'MAK', violation_code: '3', violation_name: 'EXCEEDING THE MAXIMUM TIME ALLOWED FOR THE PARKING SPACE', ticket_charges: '100', tow_charges: '250', clamp_charges: '0' },
          { city_code: 'MAK', violation_code: '4', violation_name: 'PARKING CONTRARILY WITHIN THE PAID PARKING SPACE', ticket_charges: '100', tow_charges: '250', clamp_charges: '0' },
          { city_code: 'MAK', violation_code: '5', violation_name: 'PARKING IN A RESERVED PARKING SPACE', ticket_charges: '300', tow_charges: '250', clamp_charges: '0' },
          { city_code: 'MAK', violation_code: '6', violation_name: 'PARKING IN A RESTRICTED SPACE', ticket_charges: '300', tow_charges: '250', clamp_charges: '0' },
          { city_code: 'MAK', violation_code: '7', violation_name: 'PLACING BARRIERS, OBSTRUCTIONS, FORGERY, OR CLOSING A PARKING SPACE WITHOUT A PERMIT', ticket_charges: '400', tow_charges: '250', clamp_charges: '0' },
          { city_code: 'MAK', violation_code: '8', violation_name: 'PARKING IN THE EMERGENCY DESIGNATED AREAS', ticket_charges: '900', tow_charges: '250', clamp_charges: '0' },
          { city_code: 'MAK', violation_code: '9', violation_name: 'PARKING AT THE ENTRANCES AND EXITS OF BUILDINGS', ticket_charges: '500', tow_charges: '250', clamp_charges: '0' },
          { city_code: 'MAK', violation_code: '10', violation_name: 'TOW CHARGES (Light & Heavy Vehicle)', ticket_charges: '0', tow_charges: '250/1,250', clamp_charges: '0' },
          { city_code: 'MAK', violation_code: '11', violation_name: 'STORAGE CHARGES (Light & Heavy Vehicle)', ticket_charges: '20/50 per day', tow_charges: '0', clamp_charges: '0' },
        ],
      },
    },
    tariffFallback: {
      table_key: 'makkah_on_street_tariff',
      title: 'On Street Parking Tariff (Zone Wise) excluding Vat 15%',
      data: {
        rows: [
          { zone: 'Makkah On street - Zone 1', vehicle_type: 'Car', tariff: '06 SAR', timing: '08:00 AM - 11:00 PM', working_days: 'Friday Off' },
          { zone: '', vehicle_type: 'Bus', tariff: '10 SAR', timing: '08:00 AM - 11:00 PM', working_days: 'Friday Off' },
          { zone: 'Makkah On street - Zone 2', vehicle_type: 'Car', tariff: '10 SAR', timing: '12:00 AM - 11:59 PM', working_days: '24 x 7' },
          { zone: '', vehicle_type: 'Bus', tariff: '20 SAR', timing: '12:00 AM - 11:59 PM', working_days: '24 x 7' },
          { zone: 'Makkah On street - Zone 3', vehicle_type: 'Car', tariff: '10 SAR', timing: '12:00 AM - 11:59 PM', working_days: '24 x 7' },
          { zone: '', vehicle_type: 'Bus', tariff: '20 SAR', timing: '12:00 AM - 11:59 PM', working_days: '24 x 7' },
        ],
      },
    },
    assetsFallback: {
      table_key: 'makkah_on_street_assets',
      title: 'Makkah On-Street',
      data: {
        groups: makeAssetGroups({
          ticketDispenser: '1', g10064: '1', ticketVerifier: '2', evG10064: '2',
          apms: '-', apmEts100: '-', pinPad: '-', iup250: '-',
          cardReader: '-', iur250: '-', bContactless: '-', iuc150: '-',
          rfidReader: '4', ipBarrier: '2', anprCamera: '-', arh: '-', luminera: '-',
          slidingGate: '2', right: '1', left: '1',
          pnds: '325', tpalBnaLarge: '77', tpalTx: '99', tpalV2: '149',
          poundCahier: '-', enfDevice: '121', oukitelWp2: '82', doogeeV10: '39',
          validation: '-', printer: '76', pr3: '13', sewoo: '13', bixolon: '50',
          towTruck: '13', clampVehicle: '9', supportVehicle: '4',
        }),
      },
    },
  },
  makkah_off_street_profile: {
    profileKey: 'makkah_off_street_profile',
    assetsKey: 'makkah_off_street_assets',
    pageType: 'off_street',
    profileFallback: {
      table_key: 'makkah_off_street_profile',
      title: 'Makkah Off-Street Profile',
      data: {
        total_bays: { label: 'Total Off Street Car Park Bays', value: '1,425' },
        contract_term: { value: '25 years' },
        contract_period: { start_date: '02.11.2014', end_date: '02.02.2039' },
        contract_note: { value: '(Rent payment started 19.01.2016- 15 months grace period)' },
        operational_hours: { value: '24', unit: 'Hours Per day' },
        rate_per_hour: { value: '- As per car park' },
        membership_rates: { value: 'Below for Misfalah and Jarwal' },
      },
    },
    misfalahFallback: {
      table_key: 'makkah_misfalah_membership',
      title: 'Misfalah',
      data: {
        rows: [
          { product: 'One Month', membership_price: '500', haram_staff: '200' },
          { product: 'Three Months', membership_price: '750', haram_staff: '600' },
          { product: 'Six Months', membership_price: '1,500', haram_staff: '1,200' },
          { product: 'One Years', membership_price: '3,000', haram_staff: '2,400' },
        ],
      },
    },
    jarwalFallback: {
      table_key: 'makkah_jarwal_membership_overview',
      title: 'Jarwal',
      data: {
        rows: [
          { product: 'One Month-Residential', membership_price: '350' },
          { product: 'One Month- Commercial', membership_price: '500' },
          { product: 'One Month- Corporate', membership_price: '800' },
        ],
      },
    },
    assetsFallback: {
      table_key: 'makkah_off_street_assets',
      title: 'Makkah Off-Street',
      data: {
        groups: makeAssetGroups({
          ticketDispenser: '9', g10064: '9', ticketVerifier: '9', evG10064: '4', evDipG100: '5',
          apms: '8', apmEts100: '8', pinPad: '11', iup250: '11',
          cardReader: '6', iur250: '6', bContactless: '11', iuc150: '11',
          rfidReader: '19', ipBarrier: '18', anprCamera: '18', arh: '15', luminera: '3',
          slidingGate: '-', right: '-', left: '-',
          pnds: '-', poundCahier: '-', enfDevice: '-',
          validation: '-', printer: '-',
          towTruck: '-', clampVehicle: '-', supportVehicle: '-',
        }),
      },
    },
  },
  makkah_misfalah_profile: {
    profileKey: 'makkah_misfalah_profile',
    assetsKey: 'makkah_misfalah_assets',
    pageType: 'misfalah',
    profileFallback: {
      table_key: 'makkah_misfalah_profile',
      title: 'Makkah Off-Street - Misfalah Profile',
      data: {
        total_bays: { label: 'Total Off Street Car Park Bays', value: '1,164' },
        contract_term: { value: '25 years' },
        contract_period: { start_date: '02.11.2014', end_date: '02.02.2039' },
        contract_note: { value: '(Rent payment started 19.01.2016- 15 months grace period)' },
        operational_hours: { value: '24', unit: 'Hours Per day' },
        rate_per_hour: { value: 'SAR 10.00 (1 – 5 hours); SAR 50.00 (5-24 hours) excluding vat 15% ; Repeat full day rate (Above 24 hours)' },
        membership_rates: { value: '' },
      },
    },
    membershipFallback: {
      table_key: 'makkah_misfalah_membership',
      title: 'Misfalah Membership',
      data: {
        rows: [
          { product: 'One Month', membership_price: '500', haram_staff: '200' },
          { product: 'Three Months', membership_price: '750', haram_staff: '600' },
          { product: 'Six Months', membership_price: '1,500', haram_staff: '1,200' },
          { product: 'One Years', membership_price: '3,000', haram_staff: '2,400' },
        ],
      },
    },
    assetsFallback: {
      table_key: 'makkah_misfalah_assets',
      title: 'Makkah Off-Street - Misfala',
      data: {
        groups: makeAssetGroups({
          ticketDispenser: '5', ticketVerifier: '5', evDipG100: '5',
          apms: '6', apmEts100: '6', pinPad: '11', iup250: '11',
          cardReader: '6', iur250: '6', bContactless: '11', iuc150: '11',
          rfidReader: '11', ipBarrier: '10', anprCamera: '10', arh: '7', luminera: '3',
          slidingGate: '-', right: '-', left: '-',
          pnds: '-', poundCahier: '-', enfDevice: '-',
          validation: '-', printer: '-',
          towTruck: '-', clampVehicle: '-', supportVehicle: '-',
        }),
      },
    },
  },
  makkah_jarwal_profile: {
    profileKey: 'makkah_jarwal_profile',
    assetsKey: 'makkah_jarwal_assets',
    pageType: 'jarwal',
    profileFallback: {
      table_key: 'makkah_jarwal_profile',
      title: 'Makkah Off-Street - Jarwal Profile',
      data: {
        total_bays: { label: 'Total Off Street Car Park Bays', value: '261' },
        contract_term: { value: '25 years' },
        contract_period: { start_date: '02.11.2014', end_date: '02.02.2039' },
        contract_note: { value: '(Rent payment started 19.01.2016- 15 months grace period)' },
        operational_hours: { value: '24', unit: 'Hours Per day' },
        rate_per_hour: { value: 'SAR 5.00 excluding vat 15%' },
        membership_rates: { value: '' },
      },
    },
    membershipFallback: {
      table_key: 'makkah_jarwal_membership',
      title: 'Jarwal Membership',
      data: {
        rows: [
          { product: 'One Month-Residential', membership_price: '350' },
          { product: 'One Month- Commercial', membership_price: '500' },
          { product: 'One Month- Corporate', membership_price: '800' },
        ],
      },
    },
    assetsFallback: {
      table_key: 'makkah_jarwal_assets',
      title: 'Makkah Off-Street - Jarwal',
      data: {
        groups: makeAssetGroups({
          ticketDispenser: '4', g10064: '4', ticketVerifier: '4', evG10064: '4',
          apms: '2', apmEts100: '2', pinPad: '-', iup250: '-',
          cardReader: '-', iur250: '-', bContactless: '-', iuc150: '-',
          rfidReader: '8', ipBarrier: '8', anprCamera: '8', arh: '8', luminera: '-',
          slidingGate: '-', right: '-', left: '-',
          pnds: '-', poundCahier: '-', enfDevice: '-',
          validation: '-', printer: '-',
          towTruck: '-', clampVehicle: '-', supportVehicle: '-',
        }),
      },
    },
  },
};

// ═══════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════
const MakkahProfile = ({ page, year = '', month = '', monthNumber = '', contract = '' }) => {
  const cfg = MAKKAH_PROFILE_CONFIG[page?.type];
  if (!cfg) return null;

  const hasFetchedRef = useRef(false);
  const saveRef = useRef(null);
  const { enqueue } = useApiQueue();

  const [isSaving, setIsSaving] = useState(false);
  
  // 🔥 State for second API (parking bays)
  const [parkingBaysData, setParkingBaysData] = useState(null);
  
  const [profileData, setProfileData] = useState(null);
  const [assetsData, setAssetsData] = useState(null);
  const [violationData, setViolationData] = useState(null);
  const [tariffData, setTariffData] = useState(null);
  const [misfalahOverviewData, setMisfalahOverviewData] = useState(null);
  const [jarwalOverviewData, setJarwalOverviewData] = useState(null);
  const [misfalahMembershipData, setMisfalahMembershipData] = useState(null);
  const [jarwalMembershipData, setJarwalMembershipData] = useState(null);

  // 🔥 Effective total bays - second API se aayega
  const effectiveTotalBays = parkingBaysData?.total_bays ?? profileData?.data?.total_bays?.value ?? '0';

  // Refs for profile inputs
  // 🔥 REMOVED: totalBaysRef (ab readonly hai)
  const contractTermRef = useRef(null);
  const contractStartDateRef = useRef(null);
  const contractEndDateRef = useRef(null);
  const contractNoteRef = useRef(null);
  const operationalHoursValueRef = useRef(null);
  const operationalHoursUnitRef = useRef(null);
  const parkingTariffRef = useRef(null);
  const ramadanHoursRef = useRef(null);
  const ratePerHourRef = useRef(null);
  const membershipRatesRef = useRef(null);

  // Refs for tables
  const violationRefs = useRef([]);
  const tariffRefs = useRef([]);
  const misfalahOverviewRefs = useRef([]);
  const jarwalOverviewRefs = useRef([]);
  const misfalahMembershipRefs = useRef([]);
  const jarwalMembershipRefs = useRef([]);

  // Refs for assets
  const assetSubCountRefs = useRef([]);
  const assetTotalCountRefs = useRef([]);

  // State refs for data
  const profileDataRef = useRef(null);
  const assetsDataRef = useRef(null);
  const violationDataRef = useRef(null);
  const tariffDataRef = useRef(null);
  const misfalahOverviewDataRef = useRef(null);
  const jarwalOverviewDataRef = useRef(null);
  const misfalahMembershipDataRef = useRef(null);
  const jarwalMembershipDataRef = useRef(null);

  // ── Fetch BOTH APIs ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchAllData();
  }, [year, month, monthNumber, contract]);

  const fetchAllData = async () => {
    try {
      // 🔥 Second API params based on page type
      let secondApiParams;
if (cfg.pageType === 'on_street') {
  secondApiParams = {
    contract: "1400101",
    year: year,
    month: month,
    device_onstreet: "MAK",
    type: "on_street"
  };
}else if (cfg.pageType === 'off_street') {
  secondApiParams = {
    contract: "1400102", // or whatever contract is needed for off_street overview
contract_b:"1400104",
    year: year,
    month: month,
    type: "off_street"
  };
}  else if (cfg.pageType === 'misfalah') {
  secondApiParams = {
    contract: "1400102",
    year: year,
    month: month,
    type: "off_street"
  };
} else if (cfg.pageType === 'jarwal') {
  secondApiParams = {
    contract: "1400104",
    year: year,
    month: month,
    type: "off_street"
  };
} 
      
      const [firstApiResponse, secondApiResponse] = await Promise.all([
        enqueue(() => apiCall('GET', '/boardpack/get-tables-data', { year, contract, month: monthNumber })),
        enqueue(() => apiCall('GET', '/boardpack/parking/bays', secondApiParams))
      ]);

      // Process first API response
      if (firstApiResponse?.success) {
        const data = firstApiResponse?.data || [];
        const find = (key, fb) => data.find(t => t.table_key === key) || fb;

        const profile = find(cfg.profileKey, cfg.profileFallback);
        const assets = find(cfg.assetsKey, cfg.assetsFallback);

        setProfileData(profile);
        setAssetsData(assets);
        profileDataRef.current = profile;
        assetsDataRef.current = assets;

        if (cfg.violationKey) {
          const violation = find(cfg.violationKey, cfg.violationFallback);
          setViolationData(violation);
          violationDataRef.current = violation;
        }
        if (cfg.tarifKey) {
          const tariff = find(cfg.tarifKey, cfg.tariffFallback);
          setTariffData(tariff);
          tariffDataRef.current = tariff;
        }
        if (cfg.misfalahFallback) {
          const misfalah = find('makkah_misfalah_membership', cfg.misfalahFallback);
          setMisfalahOverviewData(misfalah);
          misfalahOverviewDataRef.current = misfalah;
        }
        if (cfg.jarwalFallback) {
          const jarwal = find('makkah_jarwal_membership_overview', cfg.jarwalFallback);
          setJarwalOverviewData(jarwal);
          jarwalOverviewDataRef.current = jarwal;
        }
        if (cfg.pageType === 'misfalah' && cfg.membershipFallback) {
          const membership = find('makkah_misfalah_membership', cfg.membershipFallback);
          setMisfalahMembershipData(membership);
          misfalahMembershipDataRef.current = membership;
        }
        if (cfg.pageType === 'jarwal' && cfg.membershipFallback) {
          const membership = find('makkah_jarwal_membership', cfg.membershipFallback);
          setJarwalMembershipData(membership);
          jarwalMembershipDataRef.current = membership;
        }
      }

      // 🔥 Process second API response for parking bays
      if (secondApiResponse?.success && secondApiResponse?.data) {
        setParkingBaysData(secondApiResponse.data);
      } else {
        console.warn("Second API failed:", secondApiResponse?.message);
        setParkingBaysData(null);
      }
      
    } catch (err) {
      console.error('Fetch error:', err);
    }
  };

  // Auto-save
  const autoSave = () => {
    if (saveRef.current) clearTimeout(saveRef.current);
    saveRef.current = setTimeout(saveToAPI, API_CALL_TIME);
  };

  const transformAssetsToApiFormat = () => {
    const currentAssets = assetsDataRef.current?.data?.groups || [];
    const groups = [];
    let currentGroup = null;
    let flatIdx = 0;

    currentAssets.forEach((group) => {
      currentGroup = {
        group_name: group.group_name,
        group_total_qty: assetTotalCountRefs.current[flatIdx]?.value || group.group_total_qty || '-',
        items: []
      };
      flatIdx++;

      (group.items || []).forEach((item) => {
        currentGroup.items.push({
          item_name: item.item_name,
          qty: assetSubCountRefs.current[flatIdx]?.value || item.qty || '-'
        });
        flatIdx++;
      });

      groups.push(currentGroup);
    });

    return groups;
  };

  const saveToAPI = async () => {
    try {
      setIsSaving(true);

      // 🔥 totalBays - ab read-only value use karo
      const currentTotalBays = effectiveTotalBays;
      const currentContractTerm = contractTermRef.current?.value || profileDataRef.current?.data?.contract_term?.value || '';
      const currentStartDate = contractStartDateRef.current?.value || profileDataRef.current?.data?.contract_period?.start_date || '';
      const currentEndDate = contractEndDateRef.current?.value || profileDataRef.current?.data?.contract_period?.end_date || '';
      const currentContractNote = contractNoteRef.current?.value || profileDataRef.current?.data?.contract_note?.value || '';
      const currentOperationalHoursValue = operationalHoursValueRef.current?.value || profileDataRef.current?.data?.operational_hours?.value || '';
      const currentOperationalHoursUnit = operationalHoursUnitRef.current?.value || profileDataRef.current?.data?.operational_hours?.unit || '';
      const currentParkingTariff = parkingTariffRef.current?.value || profileDataRef.current?.data?.parking_tariff?.value || '';
      const currentRamadanHours = ramadanHoursRef.current?.value || profileDataRef.current?.data?.ramadan_hours?.value || '';
      const currentRatePerHour = ratePerHourRef.current?.value || profileDataRef.current?.data?.rate_per_hour?.value || '';
      const currentMembershipRates = membershipRatesRef.current?.value || profileDataRef.current?.data?.membership_rates?.value || '';

      const tables = [
        {
          table_key: profileDataRef.current?.table_key || cfg.profileKey,
          title: profileDataRef.current?.title || cfg.profileFallback.title,
          data: {
            total_bays: { label: profileDataRef.current?.data?.total_bays?.label || 'Total Bays', value: currentTotalBays },
            contract_term: { value: currentContractTerm },
            contract_period: { start_date: currentStartDate, end_date: currentEndDate },
            contract_note: { value: currentContractNote },
            operational_hours: { value: currentOperationalHoursValue, unit: currentOperationalHoursUnit },
            parking_tariff: { value: currentParkingTariff },
            ramadan_hours: { value: currentRamadanHours },
            rate_per_hour: { value: currentRatePerHour },
            membership_rates: { value: currentMembershipRates }
          }
        },
        {
          table_key: assetsDataRef.current?.table_key || cfg.assetsKey,
          title: assetsDataRef.current?.title || cfg.assetsFallback.title,
          data: { groups: transformAssetsToApiFormat() }
        },
      ];

      if (violationDataRef.current && cfg.violationKey) {
        const currentViolationRows = (violationDataRef.current?.data?.rows || []).map((row, rowIdx) => ({
          city_code: violationRefs.current[rowIdx]?.city_code?.value || row.city_code || '',
          violation_code: violationRefs.current[rowIdx]?.violation_code?.value || row.violation_code || '',
          violation_name: violationRefs.current[rowIdx]?.violation_name?.value || row.violation_name || '',
          ticket_charges: violationRefs.current[rowIdx]?.ticket_charges?.value || row.ticket_charges || '',
          tow_charges: violationRefs.current[rowIdx]?.tow_charges?.value || row.tow_charges || '',
          clamp_charges: violationRefs.current[rowIdx]?.clamp_charges?.value || row.clamp_charges || ''
        }));
        tables.push({
          table_key: violationDataRef.current.table_key,
          title: violationDataRef.current.title,
          data: { rows: currentViolationRows }
        });
      }

      if (tariffDataRef.current && cfg.tarifKey) {
        const currentTariffRows = (tariffDataRef.current?.data?.rows || []).map((row, rowIdx) => ({
          zone: tariffRefs.current[rowIdx]?.zone?.value || row.zone || '',
          vehicle_type: tariffRefs.current[rowIdx]?.vehicle_type?.value || row.vehicle_type || '',
          tariff: tariffRefs.current[rowIdx]?.tariff?.value || row.tariff || '',
          timing: tariffRefs.current[rowIdx]?.timing?.value || row.timing || '',
          working_days: tariffRefs.current[rowIdx]?.working_days?.value || row.working_days || ''
        }));
        tables.push({
          table_key: tariffDataRef.current.table_key,
          title: tariffDataRef.current.title,
          data: { rows: currentTariffRows }
        });
      }

      if (misfalahOverviewDataRef.current) {
        const currentRows = (misfalahOverviewDataRef.current.data.rows || []).map((row, rowIdx) => ({
          product: misfalahOverviewRefs.current[rowIdx]?.product?.value || row.product || '',
          membership_price: misfalahOverviewRefs.current[rowIdx]?.membership_price?.value || row.membership_price || '',
          haram_staff: misfalahOverviewRefs.current[rowIdx]?.haram_staff?.value || row.haram_staff || ''
        }));
        tables.push({
          table_key: misfalahOverviewDataRef.current.table_key,
          title: misfalahOverviewDataRef.current.title,
          data: { rows: currentRows }
        });
      }

      if (jarwalOverviewDataRef.current) {
        const currentRows = (jarwalOverviewDataRef.current.data.rows || []).map((row, rowIdx) => ({
          product: jarwalOverviewRefs.current[rowIdx]?.product?.value || row.product || '',
          membership_price: jarwalOverviewRefs.current[rowIdx]?.membership_price?.value || row.membership_price || ''
        }));
        tables.push({
          table_key: jarwalOverviewDataRef.current.table_key,
          title: jarwalOverviewDataRef.current.title,
          data: { rows: currentRows }
        });
      }

      if (misfalahMembershipDataRef.current) {
        const currentRows = (misfalahMembershipDataRef.current.data.rows || []).map((row, rowIdx) => ({
          product: misfalahMembershipRefs.current[rowIdx]?.product?.value || row.product || '',
          membership_price: misfalahMembershipRefs.current[rowIdx]?.membership_price?.value || row.membership_price || '',
          haram_staff: misfalahMembershipRefs.current[rowIdx]?.haram_staff?.value || row.haram_staff || ''
        }));
        tables.push({
          table_key: misfalahMembershipDataRef.current.table_key,
          title: misfalahMembershipDataRef.current.title,
          data: { rows: currentRows }
        });
      }

      if (jarwalMembershipDataRef.current) {
        const currentRows = (jarwalMembershipDataRef.current.data.rows || []).map((row, rowIdx) => ({
          product: jarwalMembershipRefs.current[rowIdx]?.product?.value || row.product || '',
          membership_price: jarwalMembershipRefs.current[rowIdx]?.membership_price?.value || row.membership_price || ''
        }));
        tables.push({
          table_key: jarwalMembershipDataRef.current.table_key,
          title: jarwalMembershipDataRef.current.title,
          data: { rows: currentRows }
        });
      }

      const res = await apiCall('POST', '/boardpack/add-tables-data', {
        contract, year, month: monthNumber, created_by: 1, tables,
      });
      if (!res?.success) throw new Error(res?.message);
      // toast.success('Data saved successfully');
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  if (!profileData || !assetsData) {
    return <LoadingComponent />;
  }

  const pd = profileData.data;
  const flatAssets = groupsToFlat(assetsData.data.groups);
  const pageType = cfg.pageType;

  return (
    <div className="min-h-screen p-8 bg-white">
      {/* Header */}
      <div className="flex justify-between items-center mb-3">
        <div className="ml-2">
          <img src={LOGO} alt="Makkah Logo" style={{ height: '50px', objectFit: 'contain' }} />
        </div>
        <h1 className="text-xl font-bold flex-1 text-center">{profileData.title}</h1>
      </div>

      {/* ON-STREET PAGE */}
      {pageType === 'on_street' && (
        <div className="flex flex-row gap-0 !border-2 !border-black p-3">
          <div className="flex-1 pr-4">
            {/* Activities */}
            <div className="mb-5">
              <p className="text-[16px] font-bold mb-6">Activities:</p>
              <div className="flex gap-0">
                {['On Street', 'Car Park', 'Enforcement'].map(label => (
                  <div key={label} className="flex flex-col items-center gap-2">
                    <div className="w-30 h-28 flex items-center justify-center">
                      {label === 'On Street' && <img src={OnStreet} alt="On Street" style={{ height: '150px', objectFit: 'contain' }} />}
                      {label === 'Car Park' && <img src={CarPark} alt="Car Park" style={{ height: '150px', objectFit: 'contain' }} />}
                      {label === 'Enforcement' && <img src={Enforcement} alt="Enforcement" style={{ height: '150px', objectFit: 'contain' }} />}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Profile Info */}
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-8 flex items-center justify-center">
                <img src={OnStreet} alt="On Street" style={{ height: '30px', objectFit: 'contain' }} />
              </div>
              <div className="flex flex-col gap-1.5">
                {/* 🔥 READ-ONLY Total Bays */}
                <span className="text-[12px]">
                  {pd.total_bays?.label}{' '}
                  <strong>
                    <input 
                      type="text" 
                      value={effectiveTotalBays}
                      readOnly 
                      disabled
                      className="font-bold outline-none bg-transparent !border-0 text-[12px] w-20 cursor-default" 
                    />
                  </strong>
                </span>
                <div className="flex items-center gap-2 ml-4">
                  <div className="w-3 h-3 !border !border-blue-600 flex-shrink-0" />
                  <span className="text-[12px] flex items-center flex-wrap gap-1">
                    Contract term{' '}
                    <strong><input type="text" defaultValue={pd.contract_term?.value || ''} ref={contractTermRef} onChange={autoSave}
                      className="font-bold outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px] w-13" /></strong>
                    {'; start date '}
                    <strong><input type="text" defaultValue={pd.contract_period?.start_date || ''} ref={contractStartDateRef} onChange={autoSave}
                      className="font-bold outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px] w-16" /></strong>
                    {' '}
                    <input type="text" defaultValue={pd.contract_note?.value || ''} ref={contractNoteRef} onChange={autoSave}
                      className="outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px] w-85" />
                    {' to '}
                    <strong><input type="text" defaultValue={pd.contract_period?.end_date || ''} ref={contractEndDateRef} onChange={autoSave}
                      className="font-bold outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px] w-24 " /></strong>
                  </span>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <div className="w-3 h-3 !border !border-blue-600 flex-shrink-0" />
                  <span className="text-[12px] flex items-center flex-wrap gap-1">
                    Operational Hours{' '}
                    <strong><input type="text" defaultValue={pd.operational_hours?.value || ''} ref={operationalHoursValueRef} onChange={autoSave}
                      className="font-bold outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px] w-48" /></strong>
                  </span>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <div className="w-3 h-3 !border !border-blue-600 flex-shrink-0" />
                  <span className="text-[12px]">
                    Parking Tariff{' '}
                    <strong><input type="text" defaultValue={pd.parking_tariff?.value || ''} ref={parkingTariffRef} onChange={autoSave}
                      className="font-bold outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px] w-48" /></strong>
                  </span>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <div className="w-3 h-3 !border !border-blue-600 flex-shrink-0" />
                  <span className="text-[12px]">
                    Ramadan Hours –{' '}
                    <strong><input type="text" defaultValue={pd.ramadan_hours?.value || ''} ref={ramadanHoursRef} onChange={autoSave}
                      className="font-bold outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px] w-24" /></strong>
                  </span>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <div className="w-3 h-3 rounded-full bg-red-500 flex-shrink-0" />
                  <span className="text-[12px]">Assets Deployed</span>
                </div>
              </div>
            </div>

            {/* Tariff and Violation Tables */}
            <div className="flex gap-4 flex-wrap">
              {tariffData && (
                <div className="">
                  <table className="!border-collapse text-[8px]">
                    <thead>
                      <tr><th colSpan={5} className="!border !border-black px-2 py-1.5 bg-gray-300 text-center font-bold text-[9px]">{tariffData.title}</th></tr>
                      <tr className="bg-gray-300">
                        <th className="!border !border-black px-2 py-1 font-bold text-center" style={{ minWidth: '110px' }}>Product</th>
                        <th className="!border !border-black px-2 py-1 font-bold text-center" style={{ minWidth: '70px' }}>Vehicle Type</th>
                        <th className="!border !border-black px-2 py-1 font-bold text-center" style={{ minWidth: '55px' }}>Tariff</th>
                        <th className="!border !border-black px-2 py-1 font-bold text-center" style={{ minWidth: '100px' }}>Timing</th>
                        <th className="!border !border-black px-2 py-1 font-bold text-center" style={{ minWidth: '70px' }}>Working Days</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tariffData.data.rows.map((row, rIdx) => {
                        const isFirstOfGroup = rIdx % 2 === 0;

                        if (!tariffRefs.current[rIdx]) tariffRefs.current[rIdx] = {};

                        return (
                          <tr key={rIdx}>
                            {isFirstOfGroup && (
                              <td rowSpan={2} className="!border !border-black px-1.5 py-0.5">
                                <input type="text" defaultValue={row.zone || ''} ref={(el) => { tariffRefs.current[rIdx].zone = el; }} onChange={autoSave} className="!border-0 bg-transparent outline-none text-[8px]" style={{ width: '110px' }} />
                              </td>
                            )}
                            <td className="!border !border-black px-1.5 py-0.5"><input type="text" defaultValue={row.vehicle_type || ''} ref={(el) => { tariffRefs.current[rIdx].vehicle_type = el; }} onChange={autoSave} className="!border-0 bg-transparent outline-none text-[8px]" style={{ width: '70px' }} /></td>
                            <td className="!border !border-black px-1.5 py-0.5"><input type="text" defaultValue={row.tariff || ''} ref={(el) => { tariffRefs.current[rIdx].tariff = el; }} onChange={autoSave} className="!border-0 bg-transparent outline-none text-[8px]" style={{ width: '55px' }} /></td>
                            <td className="!border !border-black px-1.5 py-0.5"><input type="text" defaultValue={row.timing || ''} ref={(el) => { tariffRefs.current[rIdx].timing = el; }} onChange={autoSave} className="!border-0 bg-transparent outline-none text-[8px]" style={{ width: '100px' }} /></td>
                            <td className="!border !border-black px-1.5 py-0.5"><input type="text" defaultValue={row.working_days || ''} ref={(el) => { tariffRefs.current[rIdx].working_days = el; }} onChange={autoSave} className="!border-0 bg-transparent outline-none text-[8px]" style={{ width: '70px' }} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {violationData && (
                <div className="">
                  <table className="!border-collapse text-[8px]">
                    <thead>
                      <tr><th colSpan={6} className="!border !border-black px-2 py-1.5 bg-gray-300 text-center font-bold text-[9px]">{violationData.title}</th></tr>
                      <tr className="">
                        <th className="!border !border-black px-2 py-1 font-bold text-center" style={{ minWidth: '55px' }}>City Code</th>
                        <th className="!border !border-black px-2 py-1 font-bold text-center" style={{ minWidth: '55px' }}>Violation Code</th>
                        <th className="!border !border-black px-2 py-1 font-bold text-center" style={{ minWidth: '180px' }}>Violation_Name_En</th>
                        <th className="!border !border-black px-2 py-1 font-bold text-center" style={{ minWidth: '70px' }}>Ticket Charges</th>
                        <th className="!border !border-black px-2 py-1 font-bold text-center" style={{ minWidth: '65px' }}>Tow Charges</th>
                        <th className="!border !border-black px-2 py-1 font-bold text-center" style={{ minWidth: '55px' }}>Clamp Charges</th>
                      </tr>
                    </thead>
                    <tbody>
                      {violationData.data.rows.map((row, rIdx) => {
                        if (!violationRefs.current[rIdx]) violationRefs.current[rIdx] = {};
                        return (
                          <tr key={rIdx} className={rIdx % 2 === 0 ? 'bg-white' : ''}>
                            <td className="!border !border-black px-1.5 py-0.5"><input type="text" defaultValue={row.city_code || ''} ref={(el) => { violationRefs.current[rIdx].city_code = el; }} onChange={autoSave} className="!border-0 bg-transparent outline-none text-[8px]" style={{ width: '55px' }} /></td>
                            <td className="!border !border-black px-1.5 py-0.5"><input type="text" defaultValue={row.violation_code || ''} ref={(el) => { violationRefs.current[rIdx].violation_code = el; }} onChange={autoSave} className="!border-0 bg-transparent outline-none text-[8px]" style={{ width: '55px' }} /></td>
                            <td className="!border !border-black px-1.5 py-0.5">
                              <textarea defaultValue={row.violation_name || ''} ref={(el) => { violationRefs.current[rIdx].violation_name = el; }} onChange={autoSave} rows={1} className="!border-0 bg-transparent outline-none text-[8px] resize-none" style={{ width: '180px', minHeight: '22px' }} onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }} />
                            </td>
                            <td className="!border !border-black px-1.5 py-0.5"><input type="text" defaultValue={row.ticket_charges || ''} ref={(el) => { violationRefs.current[rIdx].ticket_charges = el; }} onChange={autoSave} className="!border-0 bg-transparent outline-none text-[8px]" style={{ width: '70px' }} /></td>
                            <td className="!border !border-black px-1.5 py-0.5"><input type="text" defaultValue={row.tow_charges || ''} ref={(el) => { violationRefs.current[rIdx].tow_charges = el; }} onChange={autoSave} className="!border-0 bg-transparent outline-none text-[8px]" style={{ width: '65px' }} /></td>
                            <td className="!border !border-black px-1.5 py-0.5"><input type="text" defaultValue={row.clamp_charges || ''} ref={(el) => { violationRefs.current[rIdx].clamp_charges = el; }} onChange={autoSave} className="!border-0 bg-transparent outline-none text-[8px]" style={{ width: '55px' }} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <AssetsPanel
            assetsTable={assetsData}
            flatAssets={flatAssets}
            subCountRefs={assetSubCountRefs}
            totalCountRefs={assetTotalCountRefs}
            onSubCount={(index, value) => {
              const updatedFlat = [...flatAssets];
              updatedFlat[index] = { ...updatedFlat[index], subCount: value };
              setAssetsData(prev => ({ ...prev, data: { ...prev.data, groups: flatToGroups(updatedFlat) } }));
              assetsDataRef.current = { ...assetsDataRef.current, data: { ...assetsDataRef.current.data, groups: flatToGroups(updatedFlat) } };
              autoSave();
            }}
            onTotalCount={(index, value) => {
              const updatedFlat = [...flatAssets];
              updatedFlat[index] = { ...updatedFlat[index], totalCount: value };
              setAssetsData(prev => ({ ...prev, data: { ...prev.data, groups: flatToGroups(updatedFlat) } }));
              assetsDataRef.current = { ...assetsDataRef.current, data: { ...assetsDataRef.current.data, groups: flatToGroups(updatedFlat) } };
              autoSave();
            }}
          />
        </div>
      )}

      {/* OFF-STREET OVERVIEW PAGE */}
      {pageType === 'off_street' && (
        <div className="flex flex-row gap-0 !border-2 !border-black p-3">
          <div className="flex-1 pr-4">
            <div className="flex items-start gap-4 mb-4">
              <div className="flex flex-col items-center rounded bg-gray-50 w-20 flex-shrink-0">
                <img src={CarPark} alt="Car Park" style={{ height: '50px', objectFit: 'contain' }} />
              </div>
              <div className="flex flex-col gap-1.5">
                {/* 🔥 READ-ONLY Total Bays */}
                <span className="text-[12px]">
                  {pd.total_bays?.label}{' '}
                  <strong>
                    <input type="text" value={effectiveTotalBays} readOnly disabled className="font-bold outline-none bg-transparent !border-0 text-[12px] w-20 cursor-default" />
                  </strong>
                </span>
                <div className="flex items-center gap-2 ml-4">
                  <div className="w-3 h-3 !border !border-blue-600 flex-shrink-0" />
                  <span className="text-[12px] flex items-center flex-wrap gap-1">
                    Contract term{' '}
                    <strong><input type="text" defaultValue={pd.contract_term?.value || ''} ref={contractTermRef} onChange={autoSave} className="font-bold outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px] w-13" /></strong>
                    {'; start date '}
                    <strong><input type="text" defaultValue={pd.contract_period?.start_date || ''} ref={contractStartDateRef} onChange={autoSave} className="font-bold outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px] w-16" /></strong>
                    {' '}
                    <input type="text" defaultValue={pd.contract_note?.value || ''} ref={contractNoteRef} onChange={autoSave} className="outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px] w-85" />
                    {' to '}
                    <strong><input type="text" defaultValue={pd.contract_period?.end_date || ''} ref={contractEndDateRef} onChange={autoSave} className="font-bold outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px] w-24" /></strong>
                  </span>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <div className="w-3 h-3 !border !border-blue-600 flex-shrink-0" />
                  <span className="text-[12px] flex items-center flex-wrap gap-1">
                    Operational Hours{' '}
                    <strong><input type="text" defaultValue={pd.operational_hours?.value || ''} ref={operationalHoursValueRef} onChange={autoSave} className="font-bold outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px] w-4" /></strong>
                    {' '}
                    <strong><input type="text" defaultValue={pd.operational_hours?.unit || ''} ref={operationalHoursUnitRef} onChange={autoSave} className="font-bold outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px] w-28" /></strong>
                  </span>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <div className="w-3 h-3 !border !border-blue-600 flex-shrink-0" />
                  <span className="text-[12px]">
                    Rate per Hour{' '}
                    <strong><input type="text" defaultValue={pd.rate_per_hour?.value || ''} ref={ratePerHourRef} onChange={autoSave} className="font-bold outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px] w-80" /></strong>
                  </span>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <div className="w-3 h-3 !border !border-blue-600 flex-shrink-0" />
                  <span className="text-[12px]">
                    Membership Rates:{' '}
                    <input type="text" defaultValue={pd.membership_rates?.value || ''} ref={membershipRatesRef} onChange={autoSave} className="outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px] w-56" />
                  </span>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <div className="w-3 h-3 rounded-full bg-red-500 flex-shrink-0" />
                  <span className="text-[12px]">Assets Deployed</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-6 mt-2 ml-28">
              {misfalahOverviewData && (
                <div className="mt-4">
                  <div className='flex gap-6 items-center text-center mb-4'>
                    <div className="w-3 h-3 !border !border-blue-600 flex-shrink-0 " />
                    <p className="text-[12px] font-bold underline mb-0">Misfalah:</p>
                  </div>
                  <table className="!border-collapse text-[9px] ml-8">
                    <thead>
                      <tr className="bg-gray-300">
                        <th className="!border !border-black px-3 py-1.5 font-bold text-left" style={{ minWidth: '160px' }}>Product</th>
                        <th className="!border !border-black px-3 py-1.5 font-bold text-left" style={{ minWidth: '100px' }}>Membership Price</th>
                        <th className="!border !border-black px-3 py-1.5 font-bold text-left" style={{ minWidth: '120px' }}>Offers for Haram Authority Staff</th>
                      </tr>
                    </thead>
                    <tbody>
                      {misfalahOverviewData.data.rows.map((row, rIdx) => {
                        if (!misfalahOverviewRefs.current[rIdx]) misfalahOverviewRefs.current[rIdx] = {};
                        return (
                          <tr key={rIdx} className={rIdx % 2 === 0 ? 'bg-white' : ''}>
                            <td className="!border !border-black px-2 py-0.5"><input type="text" defaultValue={row.product || ''} ref={(el) => { misfalahOverviewRefs.current[rIdx].product = el; }} onChange={autoSave} className="!border-0 bg-transparent outline-none text-[9px] w-full" /></td>
                            <td className="!border !border-black px-2 py-0.5"><input type="text" defaultValue={row.membership_price || ''} ref={(el) => { misfalahOverviewRefs.current[rIdx].membership_price = el; }} onChange={autoSave} className="!border-0 bg-transparent outline-none text-[9px] w-full" /></td>
                            <td className="!border !border-black px-2 py-0.5"><input type="text" defaultValue={row.haram_staff || ''} ref={(el) => { misfalahOverviewRefs.current[rIdx].haram_staff = el; }} onChange={autoSave} className="!border-0 bg-transparent outline-none text-[9px] w-full" /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {jarwalOverviewData && (
                <div className="mt-4">
                  <div className='flex gap-6 items-center text-center mb-4 '>
                    <div className="w-3 h-3 !border !border-blue-600 flex-shrink-0" />
                    <p className="text-[12px] font-bold underline mb-0">Jarwal:</p>
                  </div>
                  <table className="!border-collapse text-[9px] ml-8 ">
                    <thead>
                      <tr className="bg-gray-300">
                        <th className="!border !border-black px-3 py-1.5 font-bold text-left" style={{ minWidth: '160px' }}>Product</th>
                        <th className="!border !border-black px-3 py-1.5 font-bold text-left" style={{ minWidth: '100px' }}>Membership Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jarwalOverviewData.data.rows.map((row, rIdx) => {
                        if (!jarwalOverviewRefs.current[rIdx]) jarwalOverviewRefs.current[rIdx] = {};
                        return (
                          <tr key={rIdx} className={rIdx % 2 === 0 ? 'bg-white' : ''}>
                            <td className="!border !border-black px-2 py-0.5"><input type="text" defaultValue={row.product || ''} ref={(el) => { jarwalOverviewRefs.current[rIdx].product = el; }} onChange={autoSave} className="!border-0 bg-transparent outline-none text-[9px] w-full" /></td>
                            <td className="!border !border-black px-2 py-0.5"><input type="text" defaultValue={row.membership_price || ''} ref={(el) => { jarwalOverviewRefs.current[rIdx].membership_price = el; }} onChange={autoSave} className="!border-0 bg-transparent outline-none text-[9px] w-full" /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <AssetsPanel
            assetsTable={assetsData}
            flatAssets={flatAssets}
            subCountRefs={assetSubCountRefs}
            totalCountRefs={assetTotalCountRefs}
            onSubCount={(index, value) => {
              const updatedFlat = [...flatAssets];
              updatedFlat[index] = { ...updatedFlat[index], subCount: value };
              setAssetsData(prev => ({ ...prev, data: { ...prev.data, groups: flatToGroups(updatedFlat) } }));
              assetsDataRef.current = { ...assetsDataRef.current, data: { ...assetsDataRef.current.data, groups: flatToGroups(updatedFlat) } };
              autoSave();
            }}
            onTotalCount={(index, value) => {
              const updatedFlat = [...flatAssets];
              updatedFlat[index] = { ...updatedFlat[index], totalCount: value };
              setAssetsData(prev => ({ ...prev, data: { ...prev.data, groups: flatToGroups(updatedFlat) } }));
              assetsDataRef.current = { ...assetsDataRef.current, data: { ...assetsDataRef.current.data, groups: flatToGroups(updatedFlat) } };
              autoSave();
            }}
          />
        </div>
      )}

      {/* MISFALAH PAGE */}
      {pageType === 'misfalah' && (
        <div className="flex flex-row gap-0 !border-2 !border-black p-3">
          <div className="flex-1 pr-4">
            <div className="flex items-start gap-4 mb-4 max-w-[900px]">
              <div className="flex flex-col items-center rounded bg-gray-50 w-20 flex-shrink-0">
                <img src={CarPark} alt="Car Park" style={{ height: '50px', objectFit: 'contain' }} />
              </div>
              <div className="flex flex-col gap-1.5">
                {/* 🔥 READ-ONLY Total Bays */}
                <span className="text-[12px]">
                  {pd.total_bays?.label}{' '}
                  <strong>
                    <input type="text" value={effectiveTotalBays} readOnly disabled className="font-bold outline-none bg-transparent !border-0 text-[12px] w-20 cursor-default" />
                  </strong>
                </span>
                <div className="flex items-center gap-2 ml-4">
                  <div className="w-3 h-3 !border !border-blue-600 flex-shrink-0" />
                  <span className="text-[12px] flex items-center flex-wrap gap-1">
                    Contract term{' '}
                    <strong><input type="text" defaultValue={pd.contract_term?.value || ''} ref={contractTermRef} onChange={autoSave} className="font-bold outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px] w-13" /></strong>
                    {'; start date '}
                    <strong><input type="text" defaultValue={pd.contract_period?.start_date || ''} ref={contractStartDateRef} onChange={autoSave} className="font-bold outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px] w-16" /></strong>
                    {' '}
                    <input type="text" defaultValue={pd.contract_note?.value || ''} ref={contractNoteRef} onChange={autoSave} className="outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px] w-85" />
                    {' to '}
                    <strong><input type="text" defaultValue={pd.contract_period?.end_date || ''} ref={contractEndDateRef} onChange={autoSave} className="font-bold outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px] w-24" /></strong>
                  </span>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <div className="w-3 h-3 !border !border-blue-600 flex-shrink-0" />
                  <span className="text-[12px] flex items-center flex-wrap gap-1">
                    Operational Hours{' '}
                    <strong><input type="text" defaultValue={pd.operational_hours?.value || ''} ref={operationalHoursValueRef} onChange={autoSave} className="font-bold outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px] w-4" /></strong>
                    {' '}
                    <strong><input type="text" defaultValue={pd.operational_hours?.unit || ''} ref={operationalHoursUnitRef} onChange={autoSave} className="font-bold outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px] w-28" /></strong>
                  </span>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <div className="w-3 h-3 !border !border-blue-600 flex-shrink-0" />
                  <span className="text-[12px]">
                    Rate per Hour{' '}
                    <strong><input type="text" defaultValue={pd.rate_per_hour?.value || ''} ref={ratePerHourRef} onChange={autoSave} className="font-bold outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px] w-[900px]" /></strong>
                  </span>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <div className="w-3 h-3 rounded-full bg-red-500 flex-shrink-0" />
                  <span className="text-[12px]">Assets Deployed</span>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <div className="w-3 h-3 !border !border-blue-600 flex-shrink-0" />
                  <span className="text-[12px]">Membership Rates</span>
                </div>
              </div>
            </div>

            {misfalahMembershipData && (
              <div className="mt-8">
                <table className="!border-collapse text-[9px] ml-40">
                  <thead>
                    <tr className="bg-gray-300">
                      <th className="!border !border-black px-3 py-1.5 font-bold text-left" style={{ minWidth: '160px' }}>Product</th>
                      <th className="!border !border-black px-3 py-1.5 font-bold text-left" style={{ minWidth: '100px' }}>Membership Price</th>
                      <th className="!border !border-black px-3 py-1.5 font-bold text-left" style={{ minWidth: '120px' }}>Offers for Haram Authority Staff</th>
                    </tr>
                  </thead>
                  <tbody>
                    {misfalahMembershipData.data.rows.map((row, rIdx) => {
                      if (!misfalahMembershipRefs.current[rIdx]) misfalahMembershipRefs.current[rIdx] = {};
                      return (
                        <tr key={rIdx} className={rIdx % 2 === 0 ? 'bg-white' : ''}>
                          <td className="!border !border-black px-2 py-0.5"><input type="text" defaultValue={row.product || ''} ref={(el) => { misfalahMembershipRefs.current[rIdx].product = el; }} onChange={autoSave} className="!border-0 bg-transparent outline-none text-[9px] w-full text-left" /></td>
                          <td className="!border !border-black px-2 py-0.5"><input type="text" defaultValue={row.membership_price || ''} ref={(el) => { misfalahMembershipRefs.current[rIdx].membership_price = el; }} onChange={autoSave} className="!border-0 bg-transparent outline-none text-[9px] w-full text-right" /></td>
                          <td className="!border !border-black px-2 py-0.5"><input type="text" defaultValue={row.haram_staff || ''} ref={(el) => { misfalahMembershipRefs.current[rIdx].haram_staff = el; }} onChange={autoSave} className="!border-0 bg-transparent outline-none text-[9px] w-full text-right" /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <AssetsPanel
            assetsTable={assetsData}
            flatAssets={flatAssets}
            subCountRefs={assetSubCountRefs}
            totalCountRefs={assetTotalCountRefs}
            onSubCount={(index, value) => {
              const updatedFlat = [...flatAssets];
              updatedFlat[index] = { ...updatedFlat[index], subCount: value };
              setAssetsData(prev => ({ ...prev, data: { ...prev.data, groups: flatToGroups(updatedFlat) } }));
              assetsDataRef.current = { ...assetsDataRef.current, data: { ...assetsDataRef.current.data, groups: flatToGroups(updatedFlat) } };
              autoSave();
            }}
            onTotalCount={(index, value) => {
              const updatedFlat = [...flatAssets];
              updatedFlat[index] = { ...updatedFlat[index], totalCount: value };
              setAssetsData(prev => ({ ...prev, data: { ...prev.data, groups: flatToGroups(updatedFlat) } }));
              assetsDataRef.current = { ...assetsDataRef.current, data: { ...assetsDataRef.current.data, groups: flatToGroups(updatedFlat) } };
              autoSave();
            }}
          />
        </div>
      )}

      {/* JARWAL PAGE */}
      {pageType === 'jarwal' && (
        <div className="flex flex-row gap-0 !border-2 !border-black p-3">
          <div className="flex-1 pr-4">
            <div className="flex items-start gap-4 mb-4">
              <div className="flex flex-col items-center rounded bg-gray-50 w-20 flex-shrink-0">
                <img src={CarPark} alt="Car Park" style={{ height: '50px', objectFit: 'contain' }} />
              </div>
              <div className="flex flex-col gap-1.5">
                {/* 🔥 READ-ONLY Total Bays */}
                <span className="text-[12px]">
                  {pd.total_bays?.label}{' '}
                  <strong>
                    <input type="text" value={effectiveTotalBays} readOnly disabled className="font-bold outline-none bg-transparent !border-0 text-[12px] w-20 cursor-default" />
                  </strong>
                </span>
                <div className="flex items-center gap-2 ml-4">
                  <div className="w-3 h-3 !border !border-blue-600 flex-shrink-0" />
                  <span className="text-[12px] flex items-center flex-wrap gap-1">
                    Contract term{' '}
                    <strong><input type="text" defaultValue={pd.contract_term?.value || ''} ref={contractTermRef} onChange={autoSave} className="font-bold outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px] w-13" /></strong>
                    {'; start date '}
                    <strong><input type="text" defaultValue={pd.contract_period?.start_date || ''} ref={contractStartDateRef} onChange={autoSave} className="font-bold outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px] w-16" /></strong>
                    {' '}
                    <input type="text" defaultValue={pd.contract_note?.value || ''} ref={contractNoteRef} onChange={autoSave} className="outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px] w-85" />
                    {' to '}
                    <strong><input type="text" defaultValue={pd.contract_period?.end_date || ''} ref={contractEndDateRef} onChange={autoSave} className="font-bold outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px] w-24" /></strong>
                  </span>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <div className="w-3 h-3 !border !border-blue-600 flex-shrink-0" />
                  <span className="text-[12px] flex items-center flex-wrap gap-1">
                    Operational Hours{' '}
                    <strong><input type="text" defaultValue={pd.operational_hours?.value || ''} ref={operationalHoursValueRef} onChange={autoSave} className="font-bold outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px] w-4" /></strong>
                    {' '}
                    <strong><input type="text" defaultValue={pd.operational_hours?.unit || ''} ref={operationalHoursUnitRef} onChange={autoSave} className="font-bold outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px] w-28" /></strong>
                  </span>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <div className="w-3 h-3 !border !border-blue-600 flex-shrink-0" />
                  <span className="text-[12px]">
                    Rate per Hour{' '}
                    <strong><input type="text" defaultValue={pd.rate_per_hour?.value || ''} ref={ratePerHourRef} onChange={autoSave} className="font-bold outline-none bg-transparent !border-0 border-b border-gray-300 focus:border-blue-500 text-[12px] w-80" /></strong>
                  </span>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <div className="w-3 h-3 rounded-full bg-red-500 flex-shrink-0" />
                  <span className="text-[12px]">Assets Deployed</span>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <div className="w-3 h-3 !border !border-blue-600 flex-shrink-0" />
                  <span className="text-[12px]">Membership Rates</span>
                </div>
              </div>
            </div>

            {jarwalMembershipData && (
              <div className="mt-8">
                <table className="!border-collapse text-[9px] ml-40">
                  <thead>
                    <tr className="bg-gray-300">
                      <th className="!border !border-black px-3 py-1.5 font-bold text-left" style={{ minWidth: '160px' }}>Product</th>
                      <th className="!border !border-black px-3 py-1.5 font-bold text-left" style={{ minWidth: '100px' }}>Membership Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jarwalMembershipData.data.rows.map((row, rIdx) => {
                      if (!jarwalMembershipRefs.current[rIdx]) jarwalMembershipRefs.current[rIdx] = {};
                      return (
                        <tr key={rIdx} className={rIdx % 2 === 0 ? 'bg-white' : ''}>
                          <td className="!border !border-black px-2 py-0.5"><input type="text" defaultValue={row.product || ''} ref={(el) => { jarwalMembershipRefs.current[rIdx].product = el; }} onChange={autoSave} className="!border-0 bg-transparent outline-none text-[9px] w-full" /></td>
                          <td className="!border !border-black px-2 py-0.5"><input type="text" defaultValue={row.membership_price || ''} ref={(el) => { jarwalMembershipRefs.current[rIdx].membership_price = el; }} onChange={autoSave} className="!border-0 bg-transparent outline-none text-[9px] text-right w-full" /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <AssetsPanel
            assetsTable={assetsData}
            flatAssets={flatAssets}
            subCountRefs={assetSubCountRefs}
            totalCountRefs={assetTotalCountRefs}
            onSubCount={(index, value) => {
              const updatedFlat = [...flatAssets];
              updatedFlat[index] = { ...updatedFlat[index], subCount: value };
              setAssetsData(prev => ({ ...prev, data: { ...prev.data, groups: flatToGroups(updatedFlat) } }));
              assetsDataRef.current = { ...assetsDataRef.current, data: { ...assetsDataRef.current.data, groups: flatToGroups(updatedFlat) } };
              autoSave();
            }}
            onTotalCount={(index, value) => {
              const updatedFlat = [...flatAssets];
              updatedFlat[index] = { ...updatedFlat[index], totalCount: value };
              setAssetsData(prev => ({ ...prev, data: { ...prev.data, groups: flatToGroups(updatedFlat) } }));
              assetsDataRef.current = { ...assetsDataRef.current, data: { ...assetsDataRef.current.data, groups: flatToGroups(updatedFlat) } };
              autoSave();
            }}
          />
        </div>
      )}
    </div>
  );
};

export default MakkahProfile;